import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getLoanApplication, getGetLoanApplicationQueryKey, cancelLoanApplication } from '../../../services/loanService';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { LoanApplicationStatus } from '../../../services/schemas/loanServiceSchemas/loanApplicationStatus';
import { LoanApplication } from '../../../services/schemas/loanServiceSchemas/loanApplication';
import { BpmTaskWorkItemType } from '../../../services/schemas/loanServiceSchemas';
import { Card, Badge, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import LoanApplicationComments from '../../../components/common/LoanApplicationComments';
import ApplicationWorkspace from './ApplicationWorkspace';
import LoanEditModal from '../../../components/modals/LoanEditModal';
import { Modal as BootstrapModal } from 'bootstrap';
import { toast } from 'react-toastify';
import { DECLINE_STAGE_LABELS, declineReasonLabel } from '../../../constants/declineReasons';

const ApplicationDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromDisbursement = location.state?.fromDisbursement;
  const fromVerification = location.state?.fromVerification;
  const fromCreditAssessment = location.state?.fromCreditAssessment;
  const taskId = location.state?.taskId;
  const [showEditLoanModal, setShowEditLoanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const queryClient = useQueryClient();

  const { data: application, isLoading, isError, error } = useQuery({
    queryKey: getGetLoanApplicationQueryKey(id || ''),
    queryFn: () => getLoanApplication(id || ''),
    enabled: !!id,
  });

  // Get applicable process steps based on application details
  const getApplicableProcessSteps = (): BpmTaskWorkItemType[] => {
    const steps: BpmTaskWorkItemType[] = [];
    
    // Add PROCESS_LEAD only if application started as a lead
    if (application?.loanLeadId) {
      steps.push(BpmTaskWorkItemType.PROCESS_LEAD);
    }
    
    // Core steps that always apply
    steps.push(
      BpmTaskWorkItemType.CAPTURE_APPLICATION,
      BpmTaskWorkItemType.UPLOAD_DOCUMENTS,
      BpmTaskWorkItemType.VERIFY_DOCUMENTS
    );

    // Collateral is verified after verification and before credit assessment, for a secured loan
    // with collateral captured -- the same rule the workflow routes by.
    if (application?.collaterals && application.collaterals.length > 0
        && application.loanDetails?.securityType !== 'UNSECURED') {
      steps.push(BpmTaskWorkItemType.VERIFY_COLLATERAL);
    }

    // Final steps that always apply
    steps.push(
      BpmTaskWorkItemType.CREDIT_ASSESSMENT,
      BpmTaskWorkItemType.APPROVE_LOAN,
      BpmTaskWorkItemType.UPLOAD_AGREEMENT,
      BpmTaskWorkItemType.DISBURSE_FUNDS
    );
    
    return steps;
  };

  const processSteps = application ? getApplicableProcessSteps() : [];

  // Map application status to process step
  const getCurrentStepFromStatus = (status?: LoanApplicationStatus): number => {
    if (!status) return -1;
    
    const statusToStepMap: Record<LoanApplicationStatus, BpmTaskWorkItemType> = {
      [LoanApplicationStatus.DRAFT]: BpmTaskWorkItemType.CAPTURE_APPLICATION,
      [LoanApplicationStatus.LEAD_PROCESSING]: BpmTaskWorkItemType.PROCESS_LEAD,
      [LoanApplicationStatus.PENDING_SUBMISSION]: BpmTaskWorkItemType.CAPTURE_APPLICATION,
      [LoanApplicationStatus.SUBMITTED]: BpmTaskWorkItemType.UPLOAD_DOCUMENTS,
      [LoanApplicationStatus.DOCUMENT_COLLECTION]: BpmTaskWorkItemType.UPLOAD_DOCUMENTS,
      [LoanApplicationStatus.VERIFICATION_IN_PROGRESS]: BpmTaskWorkItemType.VERIFY_DOCUMENTS,
      [LoanApplicationStatus.VERIFICATION_REJECTED]: BpmTaskWorkItemType.VERIFY_DOCUMENTS,
      [LoanApplicationStatus.VERIFICATION_COMPLETE]: BpmTaskWorkItemType.CREDIT_ASSESSMENT,
      [LoanApplicationStatus.EVALUATION_IN_PROGRESS]: BpmTaskWorkItemType.CREDIT_ASSESSMENT,
      [LoanApplicationStatus.REJECTED_EVALUATION]: BpmTaskWorkItemType.CREDIT_ASSESSMENT,
      [LoanApplicationStatus.COMMITTEE_APPROVAL]: BpmTaskWorkItemType.APPROVE_LOAN,
      [LoanApplicationStatus.REJECTED_COMMITTEE]: BpmTaskWorkItemType.APPROVE_LOAN,
      [LoanApplicationStatus.COLLATERAL_VERIFICATION]: BpmTaskWorkItemType.VERIFY_COLLATERAL,
      [LoanApplicationStatus.REJECTED_COLLATERAL]: BpmTaskWorkItemType.VERIFY_COLLATERAL,
      [LoanApplicationStatus.AGREEMENT_PREPARATION]: BpmTaskWorkItemType.UPLOAD_AGREEMENT,
      [LoanApplicationStatus.AGREEMENT_READY]: BpmTaskWorkItemType.UPLOAD_AGREEMENT,
      [LoanApplicationStatus.AGREEMENT_UPLOAD]: BpmTaskWorkItemType.UPLOAD_AGREEMENT,
      [LoanApplicationStatus.PENDING_DISBURSEMENT]: BpmTaskWorkItemType.DISBURSE_FUNDS,
      [LoanApplicationStatus.DISBURSEMENT_IN_PROGRESS]: BpmTaskWorkItemType.DISBURSE_FUNDS,
      [LoanApplicationStatus.ACTIVE]: BpmTaskWorkItemType.DISBURSE_FUNDS,
      [LoanApplicationStatus.COMPLETED]: BpmTaskWorkItemType.DISBURSE_FUNDS,
      [LoanApplicationStatus.REJECTED]: BpmTaskWorkItemType.CREDIT_ASSESSMENT,
      [LoanApplicationStatus.CANCELLED]: BpmTaskWorkItemType.CAPTURE_APPLICATION,
    };

    const currentStep = statusToStepMap[status];
    return processSteps.indexOf(currentStep);
  };

  const currentStepIndex = getCurrentStepFromStatus(application?.status);


  // Short labels keep every step of the flow on one horizontal line
  const getShortWorkItemLabel = (type: BpmTaskWorkItemType) => {
    switch (type) {
      case BpmTaskWorkItemType.PROCESS_LEAD:
        return 'Lead';
      case BpmTaskWorkItemType.CAPTURE_APPLICATION:
        return 'Capture';
      case BpmTaskWorkItemType.UPLOAD_DOCUMENTS:
        return 'Documents';
      case BpmTaskWorkItemType.VERIFY_DOCUMENTS:
        return 'Verification';
      case BpmTaskWorkItemType.CREDIT_ASSESSMENT:
        return 'Assessment';
      case BpmTaskWorkItemType.VERIFY_COLLATERAL:
        return 'Collateral';
      case BpmTaskWorkItemType.APPROVE_LOAN:
        return 'Committee';
      case BpmTaskWorkItemType.UPLOAD_AGREEMENT:
        return 'Agreement';
      case BpmTaskWorkItemType.DISBURSE_FUNDS:
        return 'Disbursement';
      default:
        return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  // Get icon for work item types
  const getWorkItemTypeIcon = (type: BpmTaskWorkItemType) => {
    switch (type) {
      case BpmTaskWorkItemType.PROCESS_LEAD:
        return 'bi-person-plus';
      case BpmTaskWorkItemType.CAPTURE_APPLICATION:
        return 'bi-file-earmark-text';
      case BpmTaskWorkItemType.UPLOAD_DOCUMENTS:
        return 'bi-cloud-upload';
      case BpmTaskWorkItemType.VERIFY_DOCUMENTS:
        return 'bi-file-check';
      case BpmTaskWorkItemType.CREDIT_ASSESSMENT:
        return 'bi-cash-stack';
      case BpmTaskWorkItemType.VERIFY_COLLATERAL:
        return 'bi-shield-check';
      case BpmTaskWorkItemType.UPLOAD_AGREEMENT:
        return 'bi-file-earmark-contract';
      case BpmTaskWorkItemType.DISBURSE_FUNDS:
        return 'bi-cash';
      default:
        return 'bi-tasks';
    }
  };

  // Handle redirection for draft applications
  useEffect(() => {
    if (application) {
      if (application.status === LoanApplicationStatus.DRAFT ||
          application.status === LoanApplicationStatus.PENDING_SUBMISSION) {
        navigate(`/loan-application/${id}`);
      }
    }
  }, [application, id, navigate]);

  const handleVerifyApplication = () => {
    if (id) {
      navigate(`/verification/${id}`);
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case LoanApplicationStatus.SUBMITTED:
        return 'primary';
      case LoanApplicationStatus.VERIFICATION_IN_PROGRESS:
      case LoanApplicationStatus.EVALUATION_IN_PROGRESS:
      case LoanApplicationStatus.AGREEMENT_PREPARATION:
      case LoanApplicationStatus.COLLATERAL_VERIFICATION:
      case LoanApplicationStatus.COMMITTEE_APPROVAL:
      case LoanApplicationStatus.DISBURSEMENT_IN_PROGRESS:
        return 'info';
      case LoanApplicationStatus.VERIFICATION_COMPLETE:
      case LoanApplicationStatus.AGREEMENT_READY:
      case LoanApplicationStatus.ACTIVE:
      case LoanApplicationStatus.COMPLETED:
        return 'success';
      case LoanApplicationStatus.REJECTED:
      case LoanApplicationStatus.REJECTED_EVALUATION:
      case LoanApplicationStatus.REJECTED_COLLATERAL:
      case LoanApplicationStatus.REJECTED_COMMITTEE:
      case LoanApplicationStatus.CANCELLED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Get status icon
  const getStatusIcon = (status?: string) => {
    switch (status) {
      case LoanApplicationStatus.SUBMITTED:
        return 'bi-file-earmark-check';
      case LoanApplicationStatus.VERIFICATION_IN_PROGRESS:
      case LoanApplicationStatus.EVALUATION_IN_PROGRESS:
      case LoanApplicationStatus.AGREEMENT_PREPARATION:
      case LoanApplicationStatus.COLLATERAL_VERIFICATION:
      case LoanApplicationStatus.COMMITTEE_APPROVAL:
      case LoanApplicationStatus.DISBURSEMENT_IN_PROGRESS:
        return 'bi-search';
      case LoanApplicationStatus.VERIFICATION_COMPLETE:
      case LoanApplicationStatus.AGREEMENT_READY:
      case LoanApplicationStatus.ACTIVE:
      case LoanApplicationStatus.COMPLETED:
        return 'bi-check-circle';
      case LoanApplicationStatus.REJECTED:
      case LoanApplicationStatus.REJECTED_EVALUATION:
      case LoanApplicationStatus.REJECTED_COLLATERAL:
      case LoanApplicationStatus.REJECTED_COMMITTEE:
      case LoanApplicationStatus.CANCELLED:
        return 'bi-x-circle';
      default:
        return 'bi-file-earmark';
    }
  };

  // Determine if cancellation is allowed
  const canCancelApplication = (status?: LoanApplicationStatus) => {
    if (!status) return false;
    const nonCancellableStatuses: LoanApplicationStatus[] = [
      LoanApplicationStatus.ACTIVE,
      LoanApplicationStatus.COMPLETED,
      LoanApplicationStatus.CANCELLED,
    ];
    return !nonCancellableStatuses.includes(status);
  };

  const handleCancelApplication = async () => {
    if (!id || !cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    setIsCancelling(true);
    try {
      await cancelLoanApplication(id, { reason: cancelReason.trim() });
      toast.success('Application cancelled successfully');
      setShowCancelModal(false);
      setCancelReason('');
      queryClient.invalidateQueries({ queryKey: getGetLoanApplicationQueryKey(id) });
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to cancel application';
      toast.error(message);
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '70vh' }}>
        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3 text-muted">Loading application details...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="m-4">
        <Alert.Heading>Error Loading Application</Alert.Heading>
        <p>We couldn't load the application details. Please try again later.</p>
        <hr />
        <p className="mb-0">Error: {(error as Error)?.message || 'Unknown error'}</p>
        <Link to="/applications" className="btn btn-outline-secondary mt-3">
          <i className="bi bi-arrow-left me-2"></i>Back to Applications
        </Link>
      </Alert>
    );
  }

  if (!application) {
    return (
      <Alert variant="warning" className="m-4">
        <Alert.Heading>Application Not Found</Alert.Heading>
        <p>The application you're looking for doesn't exist or has been removed.</p>
        <Link to="/applications" className="btn btn-outline-secondary mt-3">
          <i className="bi bi-arrow-left me-2"></i>Back to Applications
        </Link>
      </Alert>
    );
  }

  const getStatusBadgeClass = (status?: string) => {
    switch (status) {
      case LoanApplicationStatus.SUBMITTED:
        return 'bg-primary';
      case LoanApplicationStatus.VERIFICATION_IN_PROGRESS:
      case LoanApplicationStatus.EVALUATION_IN_PROGRESS:
        return 'bg-info';
      case LoanApplicationStatus.VERIFICATION_COMPLETE:
      case LoanApplicationStatus.AGREEMENT_READY:
      case LoanApplicationStatus.ACTIVE:
      case LoanApplicationStatus.COMPLETED:
        return 'bg-success';
      case LoanApplicationStatus.REJECTED:
      case LoanApplicationStatus.REJECTED_EVALUATION:
      case LoanApplicationStatus.REJECTED_COLLATERAL:
      case LoanApplicationStatus.REJECTED_COMMITTEE:
      case LoanApplicationStatus.CANCELLED:
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  return (
    <div className="container-fluid px-4 py-3">
      {/* Breadcrumb and Back Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/dashboard" className="text-decoration-none">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/applications" className="text-decoration-none">Applications</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Application Details
            </li>
          </ol>
        </nav>
        <div className="d-flex gap-2">
          {fromDisbursement && taskId ? (
            <Link to={`/disbursements/${taskId}`} className="btn btn-outline-secondary btn-sm">
              <i className="bi bi-arrow-left me-2"></i>Back to Disbursement
            </Link>
          ) : fromVerification && taskId ? (
            <Link to={`/verification/${taskId}`} className="btn btn-outline-secondary btn-sm">
              <i className="bi bi-arrow-left me-2"></i>Back to Verification
            </Link>
          ) : fromCreditAssessment && taskId ? (
            <Link to={`/credit-assessment/${taskId}`} className="btn btn-outline-secondary btn-sm">
              <i className="bi bi-arrow-left me-2"></i>Back to Credit Assessment
            </Link>
          ) : (
            <Link to="/applications" className="btn btn-outline-secondary btn-sm">
              <i className="bi bi-arrow-left me-2"></i>Back to Applications
            </Link>
          )}
          {application && canCancelApplication(application.status) && (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => setShowCancelModal(true)}
            >
              <i className="bi bi-x-circle me-2"></i>Cancel Application
            </button>
          )}
        </div>
      </div>

      {/* Application Summary Card */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-start">
                <div className="me-3">
                  <div className="rounded-circle p-3" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                    <i className={`bi ${getStatusIcon(application.status)} fs-2 text-${getStatusBadgeVariant(application.status)}`}></i>
                  </div>
                </div>
                <div>
                  <h3 className="mb-2">Application #{application.applicationNumber}</h3>
                  <p className="text-muted mb-2">
                    <i className="bi bi-building me-2"></i>
                    <strong>{application.individual?.fullNames || application.company?.companyName || 'N/A'}</strong>
                  </p>
                  <div className="d-flex align-items-center gap-3">
                    <Badge bg={getStatusBadgeVariant(application.status)} className="px-3 py-2">
                      <i className="bi bi-circle-fill me-2" style={{ fontSize: '0.6rem' }}></i>
                      {application.status?.replace(/_/g, ' ')}
                    </Badge>
                    <span className="text-muted small">
                      <i className="bi bi-calendar-event me-1"></i>
                      {formatDate(application.createdDate)}
                    </span>
                  </div>
                  {application.declineReasonCategory && (
                    <div className="mt-3 p-3 rounded border border-danger bg-danger bg-opacity-10">
                      <h6 className="text-danger mb-2">
                        <i className="bi bi-x-octagon me-2"></i>
                        Declined{application.declinedStage && ` at ${DECLINE_STAGE_LABELS[application.declinedStage] || application.declinedStage}`}
                      </h6>
                      <p className="mb-1 small"><strong>Reason:</strong> {declineReasonLabel(application.declineReasonCategory)}</p>
                      {application.declineReason && (
                        <p className="mb-1 small" style={{ whiteSpace: 'pre-line' }}><strong>Remarks:</strong> {application.declineReason}</p>
                      )}
                      {(application.declinedByName || application.declinedAt) && (
                        <p className="mb-0 small text-muted">
                          {application.declinedByName && `By ${application.declinedByName}`}
                          {application.declinedByName && application.declinedAt && ' · '}
                          {application.declinedAt && formatDate(application.declinedAt)}
                        </p>
                      )}
                    </div>
                  )}
                  {application.status === LoanApplicationStatus.CANCELLED && (
                    <div className="mt-3 p-3 rounded border border-danger bg-danger bg-opacity-10">
                      <h6 className="text-danger mb-2"><i className="bi bi-x-circle me-2"></i>Cancellation Details</h6>
                      {application.cancellationReason && (
                        <p className="mb-1 small"><strong>Reason:</strong> {application.cancellationReason}</p>
                      )}
                      {application.cancelledBy && (
                        <p className="mb-1 small"><strong>Cancelled by:</strong> {application.cancelledBy}</p>
                      )}
                      {application.cancelledAt && (
                        <p className="mb-0 small"><strong>Date:</strong> {formatDate(application.cancelledAt)}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <div className="d-flex flex-column align-items-md-end gap-2">
                {application.loanDetails?.loanAmount && (
                  <div>
                    <small className="text-muted d-block">Loan Amount</small>
                    <h4 className="text-success mb-0">{formatCurrency(application.loanDetails.loanAmount)}</h4>
                  </div>
                )}
                {application.loanDetails?.loanTerms && (
                  <small className="text-muted">
                    <i className="bi bi-calendar-range me-1"></i>
                    {application.loanDetails.numberOfInstallments === 1 
                      ? `${application.loanDetails.loanTerms} days` 
                      : `${application.loanDetails.numberOfInstallments} months`}
                  </small>
                )}
                {application?.status !== LoanApplicationStatus.CANCELLED && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    setShowEditLoanModal(true);
                    setTimeout(() => {
                      const modalElement = document.getElementById('LoanEditModal');
                      if (modalElement) {
                        const modal = new BootstrapModal(modalElement);
                        modal.show();
                      }
                    }, 100);
                  }}
                >
                  <i className="bi bi-pencil-square me-1"></i>
                  Update Loan
                </Button>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Process Flow Timeline - one horizontal run of steps so it stays a glance, not a scroll */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="px-4 py-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="mb-0">
              <i className="bi bi-diagram-3 me-2"></i>
              Application Process Flow
            </h6>
            <small className="text-muted">
              {currentStepIndex >= 0
                ? `Step ${currentStepIndex + 1} of ${processSteps.length}`
                : `${processSteps.length} applicable steps`}
            </small>
          </div>

          {/* Narrow screens scroll the row sideways rather than stacking it back into a tall list */}
          <div className="d-flex align-items-start" style={{ overflowX: 'auto' }}>
            {processSteps.map((step, index) => {
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;
              const isLast = index === processSteps.length - 1;

              return (
                <React.Fragment key={step}>
                  <div className="text-center px-2" style={{ minWidth: '104px' }}>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                      style={{
                        width: '36px',
                        height: '36px',
                        backgroundColor: isCompleted ? '#28a745' : isCurrent ? '#0d6efd' : '#e9ecef',
                        color: isCompleted || isCurrent ? 'white' : '#6c757d'
                      }}
                    >
                      {isCompleted ? (
                        <i className="bi bi-check-lg"></i>
                      ) : (
                        <i className={`bi ${getWorkItemTypeIcon(step)}`}></i>
                      )}
                    </div>
                    <div
                      className={`small mt-2 ${isCurrent ? 'fw-bold text-primary' : isCompleted ? 'text-success' : 'text-muted'}`}
                      style={{ lineHeight: 1.2 }}
                    >
                      {getShortWorkItemLabel(step)}
                    </div>
                    {isCurrent && (
                      <small className="text-primary" style={{ fontSize: '0.7rem' }}>Current step</small>
                    )}
                  </div>
                  {!isLast && (
                    <div
                      className="flex-grow-1"
                      style={{
                        height: '3px',
                        minWidth: '24px',
                        marginTop: '17px',
                        backgroundColor: isCompleted ? '#28a745' : '#e9ecef',
                        borderRadius: '2px'
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {(!application?.loanLeadId || !application?.collaterals?.length) && (
            <small className="text-muted d-block mt-3">
              <i className="bi bi-info-circle me-1"></i>
              Not applicable to this application:
              {!application?.loanLeadId && ' Process Lead (no lead data)'}
              {!application?.loanLeadId && !application?.collaterals?.length && ','}
              {!application?.collaterals?.length && ' Verify Collateral (no collaterals)'}
            </small>
          )}
        </Card.Body>
      </Card>

      {/* Application workspace: the funding request, its documents and the customer behind it */}
      <ApplicationWorkspace application={application} />

      <Row className="g-4">
        <Col lg={12}>
          {/* Comments Section */}
          {application && (
            <LoanApplicationComments
              loanApplicationId={application.id!}
              currentStage={application.status || 'DRAFT'}
              className="mt-4"
            />
          )}
        </Col>
      </Row>

      {/* Update Loan Modal */}
      {showEditLoanModal && application && (
        <div className="modal fade" id="LoanEditModal" tabIndex={-1} aria-hidden="true">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <LoanEditModal
              applicationId={application.id || ''}
              loan={{
                loanAmount: application.loanDetails?.loanAmount,
                interestRate: application.loanDetails?.interestRate,
                dueDate: application.loanDetails?.dueDate,
                commencementDate: application.loanDetails?.commencementDate,
                adminFee: application.loanDetails?.adminFee,
                customerName: application.individual?.fullNames || application.company?.companyName,
                accountNumber: application.applicationNumber,
              }}
              onSuccess={() => {
                // Invalidate the query to refetch fresh data
                queryClient.invalidateQueries({ queryKey: getGetLoanApplicationQueryKey(id || '') });
                setShowEditLoanModal(false);
              }}
              onClose={() => setShowEditLoanModal(false)}
            />
          </div>
        </div>
      )}

      {/* Cancel Application Modal */}
      {showCancelModal && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.3)' }}>
                    <i className="bi bi-x-circle text-danger" style={{ fontSize: '1.2rem' }}></i>
                  </div>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold">Cancel Application</h5>
                    <p className="text-muted mb-0 small">This action cannot be undone</p>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)} disabled={isCancelling}></button>
              </div>
              <div className="modal-body p-4">
                <p className="text-muted mb-3">
                  Are you sure you want to cancel this application? This will terminate any active processes and mark the application as cancelled.
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Reason for Cancellation <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Please provide a reason for cancelling this application..."
                    disabled={isCancelling}
                    maxLength={500}
                  />
                  <small className="text-muted">{cancelReason.length}/500 characters</small>
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-pill px-4"
                  onClick={() => setShowCancelModal(false)}
                  disabled={isCancelling}
                >
                  Go Back
                </button>
                <button
                  type="button"
                  className="btn btn-danger rounded-pill px-4 fw-semibold"
                  onClick={handleCancelApplication}
                  disabled={isCancelling || !cancelReason.trim()}
                >
                  {isCancelling ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-x-circle me-2"></i>
                      Cancel Application
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicationDetails;
